import React, { useState, useContext, useRef } from "react";
import useForm from "react-hook-form";

import AppBar from "@material-ui/core/AppBar";
import axios from "axios";
import Button from "@material-ui/core/Button";
import { Grid } from "@material-ui/core";
import { Link, Redirect } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import ReactGA from "react-ga";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";

import config from "../../dashboard-config";

/* context for hooks */
import { SessionContext } from "../../context/SessionContext";

import "./Session.scss";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexWrap: "wrap"
  },
  closeButton: {
    textAlign: "right"
  },
  formContainer: {
    marginTop: "2vh"
  },
  inputLabel: {
    fontWeight: 900,
    fontSize: "1.1em",
    paddingLeft: "0.5vw"
  },
  linkContainer: {
    padding: "10px 0 30px 0"
  },
  link: {
    textAlign: "right"
  },
  loginPane: {
    backgroundColor: "#228CBD",
    borderRadius: "2px",
    color: "#FFF",
    fontSize: "0.8em",
    maxWidth: "100vw",
    minHeight: "65vh",
    padding: "8vh 5vw",
    "@media only screen and (min-width: 600px)": {
      fontSize: "1em",
      minHeight: "65vh",
      maxWidth: "22vw"
    },
    "@media only screen and (max-height: 800px)": {
      minHeight: "75vh"
    }
  },
  register: {
    backgroundColor: "#228CBD",
    padding: "20px",
    marginLeft: "0.5vw",
    width: "90%",

    "&:hover": {
      backgroundColor: "#262A3F"
    },
    "@media only screen and (max-height: 950px)": {
      fontSize: "0.9em",
      padding: "10px",
      width: "90%"
    }
  },
  registerForm: {
    backgroundColor: "#FFF",
    borderRadius: "2px",
    maxWidth: "100vw",
    minHeight: "65vh",
    padding: "5vh 5vw 0 5vw",
    "@media only screen and (min-width: 600px)": {
      maxWidth: "50vw"
    },
    "@media only screen and (min-width: 1000px)": {
      maxWidth: "30vw"
    },
    "@media only screen and (max-height: 950px)": {
      fontSize: "0.9em",
      minWidth: "40vw",
      padding: "3vh 3vw 0 3vw"
    },
    "@media only screen and (max-height: 800px)": {
      fontSize: "0.7em",
      minWidth: "45vw",
      minHeight: "75vh",
      padding: "3vh 3vw 0 3vw"
    }
  },
  sofarQuote: {
    fontSize: "1em",
    fontWeight: "900",
    padding: "4vh 0 5vh 0",
    "@media only screen and (min-width: 600px)": {
      fontSize: "1em",
      padding: "4vh 0 5vh 0"
    },
    "@media only screen and (min-width: 1200px)": {
      fontSize: "1.1em",
      padding: "6vh 0 10vh 0"
    },
    "@media only screen and (min-width: 1500px)": {
      fontSize: "1.2em",
      padding: "10vh 0 16vh 0"
    }
  },
  termsBar: {
    background: "rgba(37, 42, 63, 0.5)",
    bottom: "0",
    fontSize: "0.8em",
    marginRight: theme.spacing(2),
    position: "absolute"
  },
  termLinks: {
    marginLeft: "auto",
    textDecoration: "none"
  },
  textField: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: "90%"
  }
}));

const Register = () => {
  const classes = useStyles();
  const [isRegistered, setIsRegistered] = useState("");
  const [disclosure, setDisclosure] = useState(false);
  const [isError, setIsError] = useState(false);
  const { currentSession, setCurrentSession } = useContext(SessionContext);
  const { register, errors, handleSubmit, watch } = useForm({});

  const password = useRef({});
  password.current = watch("password", "");

  const postRegister = async data => {
    /* URI encoding object */
    const registerDetails = {
      email: data.email,
      password: data.password,
      reEnterPassword: data.password_repeat
    };
    const loginDetails = {
      username: data.email,
      password: data.password,
      skipRedirect: true
    };

    let formBody = [];
    for (let property in registerDetails) {
      let encodedKey = encodeURIComponent(property);
      let encodedValue = encodeURIComponent(registerDetails[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    formBody = formBody.join("&");

    try {
      const registerResult = await fetch(
        `${config.wavefleetUrl}/oceans-register/`,
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/x-www-form-urlencoded"
          }),
          body: formBody
        }
      );

      if (registerResult.status === 200) {
        setIsRegistered(true);

        formBody = [];
        for (let property in loginDetails) {
          let encodedKey = encodeURIComponent(property);
          let encodedValue = encodeURIComponent(loginDetails[property]);
          formBody.push(encodedKey + "=" + encodedValue);
        }
        formBody = formBody.join("&");

        // TODO should get profile from oceans-register/ endpoint and login instead of making another HTTP request
        const loginResult = await axios.post(
          `${config.wavefleetUrl}/login/`,
          formBody,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (loginResult.status === 200) {
          localStorage.setItem(
            "sofar-user",
            JSON.stringify({
              token: loginResult.data.token,
              username: loginResult.data.username
            })
          );
          setCurrentSession({
            ...currentSession,
            isLogged: true
          });
        } else {
          setIsError(true);
        }
      } else {
        setIsError(true);
      }
    } catch (e) {
      console.error(e);
      setIsError(true);
    }
  };

  if (isRegistered && currentSession.isLogged) {
    ReactGA.event({
      category: "Register",
      action: "Successfully Registered!"
    });
    return currentSession.redirectAfterLogin ? (
      <Redirect to={currentSession.redirectAfterLogin.pathname} />
    ) : (
      <Redirect to="/" />
    );
  }

  if (currentSession.isLogged) {
    return <Redirect to="/" />;
  }

  return (
    <div className="auth-container">
      <Grid
        container
        spacing={0}
        alignItems="center"
        justify="center"
        style={{ minHeight: "94vh" }}
      >
        <Grid item xs={8} className={classes.registerForm}>
          <Typography
            display="inline"
            variant="h3"
            style={{ fontWeight: "900", paddingLeft: "0.5vw" }}
          >
            Sign up
          </Typography>

          {isError && (
            <Typography
              className={classes.linkContainer}
              data-test="emailTakenError"
            >
              Email is already taken!
            </Typography>
          )}

          <form className={classes.formContainer} noValidate autoComplete="off">
            <Typography variant="h6" className={classes.inputLabel}>
              Email Address
            </Typography>
            <TextField
              id="standard-basic"
              className={classes.textField}
              fullWidth={true}
              // label="Email"
              name="email"
              margin="normal"
              variant="outlined"
              inputRef={register({
                required: "Required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid Email Address"
                }
              })}
              data-test="emailInput"
            />
            {errors.email && <Typography>{errors.email.message}</Typography>}

            <Typography variant="h6" className={classes.inputLabel}>
              Password
            </Typography>
            <TextField
              id="standard-basic"
              className={classes.textField}
              fullWidth={true}
              // label="Password"
              name="password"
              margin="normal"
              type="password"
              variant="outlined"
              inputRef={register({
                required: "You must specify a password",
                minLength: {
                  value: 8,
                  message: "Password must have at least 8 characters"
                }
              })}
              data-test="passwordInput"
            />
            {errors.password && (
              <Typography>{errors.password.message}</Typography>
            )}

            <Typography variant="h6" className={classes.inputLabel}>
              Confirm Password
            </Typography>
            <TextField
              id="standard-basic"
              className={classes.textField}
              fullWidth={true}
              // label="Confirm Password"
              name="password_repeat"
              margin="normal"
              type="password"
              variant="outlined"
              inputRef={register({
                validate: value =>
                  value === password.current || "The passwords do not match"
              })}
              data-test="confirmPasswordInput"
            />
            {errors.password_repeat && (
              <Typography>{errors.password_repeat.message}</Typography>
            )}

            <div style={{ margin: "auto", padding: "20px 0.5vw" }}>
              <input
                type="checkbox"
                id="cbx"
                style={{ display: "none" }}
                onChange={e => setDisclosure(!disclosure)}
              />
              <label
                htmlFor="cbx"
                className="check"
                data-test="agreeToTermsCheck"
              >
                <svg width="18px" height="18px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>

              <Typography
                display="inline"
                variant="subtitle1"
                style={{ color: "#262A3F", fontSize: "0.8em" }}
              >
                I agree to adhere to Sofar Ocean Technologies'{" "}
                <a
                  href="https://www.sofarocean.com/posts/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://www.sofarocean.com/posts/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  Terms of Use
                </a>
              </Typography>
            </div>
          </form>

          {disclosure && (
            <Button
              variant="contained"
              size="large"
              color="primary"
              className={classes.register}
              onClick={handleSubmit(postRegister)}
              data-test="submitButton"
            >
              Get started
            </Button>
          )}
        </Grid>
        <Grid item xs={4} className={classes.loginPane}>
          <Typography variant="subtitle1" className={classes.sofarQuote}>
            Get access to the largest, real-time open ocean weather sensor
            network.
          </Typography>
          <Typography variant="subtitle1" style={{ fontSize: "0.9em" }}>
            Already have an account?
          </Typography>
          <Typography variant="subtitle1" style={{ fontSize: "0.8em" }}>
            <Link to="/login" style={{ textDecoration: "none" }}>
              Sign in here
            </Link>
          </Typography>
        </Grid>
      </Grid>
      <AppBar className={classes.termsBar} position="static">
        <Toolbar>
          <span>© Sofar Ocean Technologies</span>
          <span className={classes.termLinks}>
            <a
              href="https://www.sofarocean.com/posts/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#FFF",
                padding: "0 1vw",
                textDecoration: "none"
              }}
            >
              Privacy Policy
            </a>
            <a
              href="https://www.sofarocean.com/posts/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#FFF",
                padding: "0 1vw",
                textDecoration: "none"
              }}
            >
              Terms of Use
            </a>
          </span>
        </Toolbar>
      </AppBar>
    </div>
  );
};

export default Register;
